import { z } from 'zod';

/**
 * `inference.v1`: the user's model providers, as the core serves them. A provider plugin declares
 * its API (`provides[].api`: a format and the URL of its models); the core lists the models of
 * every provider and calls each API in its format, signed by the provider's connection. A model
 * is chosen with its provider (`{ provider, model }`): two providers may list the same id.
 * `credentials` is the OpenAI-compatible endpoint the agents the core starts are configured with.
 */

/** What a model takes (OpenRouter's vocabulary). */
export const MODEL_INPUTS = ['text', 'image', 'audio', 'file', 'video'] as const;
/**
 * What a model makes: `speech` reads text aloud, `transcription` writes what a recording says,
 * `realtime-transcription` what is said as it is said (a voice call's listening).
 */
export const MODEL_OUTPUTS = [
  'text',
  'image',
  'audio',
  'speech',
  'transcription',
  'realtime-transcription',
  'embeddings',
] as const;
export type ModelInput = (typeof MODEL_INPUTS)[number];
export type ModelOutput = (typeof MODEL_OUTPUTS)[number];

const providerSchema = z.string().min(1).max(256);

const modelSchema = z
  .object({
    id: z.string().min(1).max(256),
    name: z.string().min(1).max(256),
    /** The plugin whose API serves it. */
    provider: providerSchema,
    /** That provider's name, for pickers (`Model · Provider`). */
    providerTitle: z.string().min(1).max(256),
    /** Who made the model (`openai`, `anthropic`…), when the provider says. */
    owner: z.string().max(128).optional(),
    input: z.array(z.enum(MODEL_INPUTS)).max(8),
    output: z.array(z.enum(MODEL_OUTPUTS)).max(8),
  })
  .strict();
export type InferenceModel = z.infer<typeof modelSchema>;

/** A model and the provider serving it; either may be left to the user's defaults. */
const choiceSchema = {
  model: z.string().min(1).max(256).optional(),
  provider: providerSchema.optional(),
};

/** Image types a provider takes and makes. */
const imageTypeSchema = z.enum(['image/png', 'image/jpeg', 'image/webp']);
/** An image in base64: 20 MB of characters, about 15 MB of picture. */
const imageSchema = z
  .object({ data: z.string().min(1).max(20_000_000), mimeType: imageTypeSchema })
  .strict();
export type InferenceImage = z.infer<typeof imageSchema>;

/** A made image, with the prompt the model rewrote it from, when it says. */
const generatedImageSchema = z
  .object({
    data: z.string().min(1).max(20_000_000),
    mimeType: imageTypeSchema,
    revisedPrompt: z.string().max(32_000).optional(),
  })
  .strict();
export type GeneratedImage = z.infer<typeof generatedImageSchema>;

/** What an image is made with: its prompt, shape and how many. */
const imageRequest = {
  prompt: z.string().min(1).max(32_000),
  /** `1024x1024`, or `auto` for the model's choice. */
  size: z.union([z.literal('auto'), z.string().regex(/^\d{3,4}x\d{3,4}$/)]).optional(),
  quality: z.enum(['auto', 'low', 'medium', 'high']).optional(),
  background: z.enum(['auto', 'transparent', 'opaque']).optional(),
  count: z.number().int().min(1).max(4).default(1),
  /** Images take time: up to five minutes. */
  timeoutMs: z.number().int().min(1000).max(300_000).default(180_000),
  ...choiceSchema,
};
const imagesOutputSchema = z
  .object({ images: z.array(generatedImageSchema).min(1).max(4) })
  .strict();

/** Audio in base64: 14 MB of characters, about 10 MB of sound. */
const audioSchema = z.string().min(1).max(14_000_000);
const audioTypeSchema = z.enum(['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/mp4']);

/** A word of a transcription and when it is said, from the start of the audio. */
const wordSchema = z
  .object({
    word: z.string().max(256),
    startMs: z.number().int().min(0),
    endMs: z.number().int().min(0),
  })
  .strict();
export type TranscribedWord = z.infer<typeof wordSchema>;

export const inferenceV1 = {
  'models.list': {
    input: z.object({}).strict(),
    output: z
      .object({
        models: z.array(modelSchema).max(2000),
        /** The providers whose models could not be listed, and why (a key refused…). */
        problems: z
          .array(
            z
              .object({
                provider: providerSchema,
                providerTitle: z.string().min(1).max(256),
                message: z.string().min(1).max(500),
              })
              .strict(),
          )
          .max(50)
          .optional(),
      })
      .strict(),
  },
  /**
   * The endpoint an agent the core starts is configured with: the provider the user chose for that
   * agent (Settings › Agents) and its model, else the provider of their default text model. Null
   * when the agent uses its own account.
   */
  credentials: {
    input: z
      .object({
        /** The agent's catalog id (`claude-acp`). */
        agent: z.string().min(1).max(256).optional(),
      })
      .strict(),
    output: z
      .object({
        baseUrl: z.url().max(2048),
        token: z.string().min(1).max(8192),
        /** The model the user chose for the agent. */
        model: z.string().min(1).max(256).optional(),
      })
      .strict()
      .nullable(),
  },
  /**
   * A text written once, by a text model: what it is told (`instructions`) and given (`input`),
   * shaped by `outputSchema` (a JSON Schema) when the answer must be JSON.
   */
  'text.generate': {
    input: z
      .object({
        instructions: z.string().max(100_000).optional(),
        input: z.string().max(500_000),
        outputSchema: z.record(z.string(), z.unknown()).optional(),
        maxOutputTokens: z.number().int().min(1).max(1_000_000).optional(),
        /** The model's own levels (`low`, `high`…), when it reasons. */
        reasoningEffort: z.string().min(1).max(32).optional(),
        timeoutMs: z.number().int().min(1000).max(300_000).default(120_000),
        ...choiceSchema,
      })
      .strict(),
    output: z.object({ text: z.string().max(2_000_000) }).strict(),
  },
  /** Images made from a prompt, by an image model. */
  'images.generate': {
    input: z.object(imageRequest).strict(),
    output: imagesOutputSchema,
  },
  /**
   * Images made from others and a prompt, by a model that takes images: `images` are edited or
   * followed as references; `mask`'s transparent pixels say where the first one changes.
   */
  'images.edit': {
    input: z
      .object({
        ...imageRequest,
        images: z.array(imageSchema).min(1).max(16),
        mask: imageSchema.optional(),
      })
      .strict(),
    output: imagesOutputSchema,
  },
  /** The voices a speech model reads with, from its provider. */
  'voices.list': {
    input: z.object(choiceSchema).strict(),
    output: z
      .object({
        voices: z
          .array(z.object({ id: z.string().min(1).max(256), name: z.string().min(1).max(256) }))
          .max(1000),
      })
      .strict(),
  },
  /** Text read aloud. */
  speech: {
    input: z
      .object({
        text: z.string().min(1).max(5000),
        /** A voice of the model's provider, by id or by name; its default otherwise. */
        voice: z.string().min(1).max(128).optional(),
        ...choiceSchema,
        /** Words per minute relative to normal: 0.25 to 4. */
        speed: z.number().min(0.25).max(4).optional(),
        /** MP3 by default; WAV (16-bit PCM) for what plays it without a decoder (a call's room). */
        format: z.enum(['mp3', 'wav']).optional(),
      })
      .strict(),
    output: z.object({ audio: audioSchema, mimeType: audioTypeSchema }).strict(),
  },
  /**
   * A live transcription the caller opens itself (a voice call's listening): the WebSocket to
   * open, with the short-lived credentials it takes (`protocols`), and the wire protocol it speaks
   * (`mistral`: Voxtral Realtime's, turns included). The provider's key never reaches the caller.
   */
  'transcription.realtime': {
    input: z
      .object({
        /** ISO 639-1 (`fr`), when known; detected otherwise. */
        language: z
          .string()
          .regex(/^[a-z]{2}$/)
          .optional(),
        ...choiceSchema,
      })
      .strict(),
    output: z
      .object({
        protocol: z.enum(['mistral']),
        url: z.url({ protocol: /^wss?$/ }).max(2048),
        /** The WebSocket's subprotocols, which carry its credentials. */
        protocols: z.array(z.string().min(1).max(4096)).max(4),
        model: z.string().min(1).max(256),
      })
      .strict(),
  },
  /** The words of a recording and when each is said. */
  transcription: {
    input: z
      .object({
        audio: audioSchema,
        mimeType: audioTypeSchema,
        /** ISO 639-1 (`fr`), when known. */
        language: z
          .string()
          .regex(/^[a-z]{2}$/)
          .optional(),
        ...choiceSchema,
      })
      .strict(),
    output: z
      .object({
        text: z.string().max(100_000),
        /** The audio's length. */
        durationMs: z.number().int().min(0),
        words: z.array(wordSchema).max(20_000),
      })
      .strict(),
  },
} as const;
